import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "./Api/api";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import OrderSummary from "./OrderSummary";
import { loadStripe } from "@stripe/stripe-js";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckOut = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [ono, setOno] = useState();
  const [send, setSend] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("online"); // Default to online
  const [orderType, setOrderType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deliveryCharge, setDeliveryCharge] = useState(0);

  const cartreduxdata = useSelector((state) => state.cart);
  const packagesreduxdata = useSelector((state) => state.packages);
  const dressdata = useSelector((state) => state.innerdress);
  const personalisedreduxdata = useSelector((state) => state.personalised);
  const servicedata = useSelector((state) => state.services);

  const { data: custdata, refetch: refetchcust } = useQuery(
    ["cust"],
    () => api.get(`users/getcustomers/${user?.id}`),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!user?.id,
      select: (res) => res.data,
    }
  );

  const { data: orderdata } = useQuery(["orderdata"], () =>
    api.get(`users/getorders`).then((res) => res.data)
  );

  const { data: maildata, refetch: refetchmail } = useQuery(
    ["mail"],
    () =>
      api.get(
        `users/getmail/${user?.id}/${ono}/${custdata?.data?.name}/${custdata?.data?.last_name}/${custdata?.data?.address}/${gtotal}/${dressdata?.name}/${dressdata?.jsonimage?.picture0}/${dressdata?.price}/${cartreduxdata?.length}`
      ),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: false,
      select: (res) => res.data,
    }
  );

  // ✅ Authentication check - FIRST useEffect
  useEffect(() => {
    const userFromStorage = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!userFromStorage || !token) {
      console.error("⚠️ No user found in localStorage, redirecting to login");
      dispatch({ type: "CHECKOUT_STATUS", payload: 1 });
      navigate("/login");
      return;
    }

    try {
      const userData = JSON.parse(userFromStorage);
      setUser(userData);
      console.log("✅ User loaded in Checkout:", userData);
    } catch (e) {
      console.error("Error parsing user data:", e);
      navigate("/login");
    }
  }, [navigate, dispatch]);

  // ✅ Get order type from localStorage
  useEffect(() => {
    const storedOrderType = localStorage.getItem("order_type");
    setOrderType(storedOrderType);
    console.log("📦 Order type loaded:", storedOrderType);
  }, []);

  // Get EasyPost shipping charge from Redux or localStorage
  useEffect(() => {
    // First check Redux servicedata
    if (servicedata && servicedata.rate) {
      setDeliveryCharge(Number(servicedata.rate));
      console.log("Using shipping from Redux:", servicedata);
    } else {
      // Fallback to localStorage
      const storedShipping = localStorage.getItem("easypostShipping");
      if (storedShipping) {
        try {
          const shippingData = JSON.parse(storedShipping);
          if (shippingData.rate) {
            setDeliveryCharge(Number(shippingData.rate));
            console.log("Using shipping from localStorage:", shippingData);
          }
        } catch (e) {
          console.error("Error parsing shipping data:", e);
          setDeliveryCharge(0);
        }
      } else {
        setDeliveryCharge(0);
      }
    }
  }, [servicedata]);

  useEffect(() => {
    if (user?.id) {
      setTimeout(() => {
        refetchcust();
      }, 1000);
    }
  }, [user, refetchcust]);

  const mutationpackage = useMutation(
    (body) => api.post("users/addtopackage", body),
    {
      onError: (error) => console.log(error),
      onSuccess: () => {
        setTimeout(() => {
          navigate("/thankyouorderplace");
        }, 1000);
      },
    }
  );

  const mutationorderproducts = useMutation(
    (body) => api.post("users/orderproducts", body),
    {
      onError: (error) => console.log(error),
    }
  );

  // Stripe Payment Mutation
  const mutationStripePayment = useMutation(
    (orderData) => api.post("users/create-stripe-session", orderData),
    {
      onSuccess: async (res) => {
        try {
          console.log("Stripe session response:", res.data);
          if (res.data.status && res.data.sessionId) {
            const stripe = await stripePromise;
            if (stripe) {
              // Redirect to Stripe Checkout
              const { error } = await stripe.redirectToCheckout({
                sessionId: res.data.sessionId,
              });
              if (error) {
                console.error("Stripe redirect error:", error);
                setError("Payment redirect failed: " + error.message);
                setIsLoading(false);
              }
            } else {
              setError("Stripe failed to initialize");
              setIsLoading(false);
            }
          } else {
            setError(res.data.error || "Failed to create checkout session");
            setIsLoading(false);
          }
        } catch (err) {
          console.error("Stripe processing error:", err);
          setError("Payment processing failed");
          setIsLoading(false);
        }
      },
      onError: (err) => {
        console.error("Error creating Stripe session:", err);
        const errorMessage =
          err.response?.data?.error || err.message || "Payment failed";
        setError(errorMessage);
        setIsLoading(false);
      },
    }
  );

  // Regular Order Placement (for offline/COD)
  const mutationorderplace = useMutation(
    (body) => api.post("users/orderplace", body),
    {
      onError: (error) => {
        console.log("Order placement error:", error);
        setError(
          "Order placement failed: " +
            (error.response?.data?.error || error.message)
        );
        setIsLoading(false);
      },
      onSuccess: (response) => {
        console.log("Order placed successfully:", response.data);
        const count = 10000 + parseInt(orderdata?.data?.length || 0) + 1;
        const orderno = "SLR" + count;
        setOno(orderno);

        // Save products
        cartreduxdata?.forEach((item) => {
          mutationorderproducts.mutate({
            ...item,
            userid: user?.id,
            orderid: orderno,
          });
        });

        // Save packages
        let temppackages = packagesreduxdata?.map((item) => ({
          ...item,
          userid: user?.id,
          orderid: orderno,
        }));

        if (personalisedreduxdata && personalisedreduxdata.length > 0) {
          let tempPersonalisedPackages = personalisedreduxdata?.map((item) => ({
            ...item,
            userid: user?.id,
            orderid: orderno,
            ustring: cartreduxdata[0]?.packages,
          }));
          temppackages = [...temppackages, ...tempPersonalisedPackages];
        }

        temppackages?.forEach((item) => {
          if (item?.cartquantity && item.name) {
            mutationpackage.mutate(item);
          }
        });

        refetchmail();
        setSend(true);
        setIsLoading(false);
      },
    }
  );

  useEffect(() => {
    if (maildata?.status && send) {
      setTimeout(() => {
        navigate("/thankyouorderplace");
      }, 1000);
    }
  }, [maildata, send, navigate]);

  const total =
    cartreduxdata?.reduce((sum, item) => sum + (item.cartamount || 0), 0) || 0;
  const packtotal =
    packagesreduxdata?.reduce((sum, item) => sum + (item.cartamount || 0), 0) ||
    0;
  const personalisedtotal =
    personalisedreduxdata?.reduce(
      (sum, item) => sum + (item.cartamount || 0),
      0
    ) || 0;

  // Calculate grand total with EasyPost shipping
  const gtotal = Number(
    (total + packtotal + personalisedtotal + deliveryCharge).toFixed(2)
  );

  const handlePlaceOrder = () => {
    // Clear any previous errors
    setError(null);
    setIsLoading(true);

    // Validation
    if (!user?.id) {
      setError("User not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    if (gtotal <= 0) {
      setError("Order total must be greater than zero.");
      setIsLoading(false);
      return;
    }

    // ✅ FIXED: Get order type from localStorage
    const currentOrderType = localStorage.getItem("order_type") || "delivery";

    // ✅ FIXED: Get appropriate ID based on order type
    let deliveryId = null;
    let collectionId = null;

    if (currentOrderType === "delivery") {
      deliveryId = localStorage.getItem("delivery_id");

      // ✅ FIXED: Only check custdata for delivery orders
      if (!deliveryId && !custdata?.data?.id) {
        setError(
          "Delivery information not found. Please add your delivery details first."
        );
        setIsLoading(false);
        return;
      }

      // Use custdata.id as deliveryId if delivery_id is not in localStorage
      deliveryId = deliveryId || custdata?.data?.id;
    } else if (currentOrderType === "collection") {
      collectionId = localStorage.getItem("collection_id");

      // ✅ FIXED: For collection, we don't need custdata
      if (!collectionId) {
        setError(
          "Collection request not found. Please submit collection details first."
        );
        setIsLoading(false);
        return;
      }
    }


    const count = 10000 + parseInt(orderdata?.data?.length || 0) + 1;
    const orderno = "SLR" + count;
    setOno(orderno);

    // ✅ FIXED: Get customerId from localStorage
    const customerId = localStorage.getItem("customerId");

    console.log("Placing order with:", {
      userid: user.id,
      totalamount: gtotal,
      customerid: customerId, // ✅ Use from localStorage
      orderno: orderno,
      paymentMethod: paymentMethod,
      deliveryCharge: deliveryCharge,
      orderType: currentOrderType,
      deliveryId: deliveryId,
      collectionId: collectionId,
    });

    if (paymentMethod === "online") {
      // STRIPE PAYMENT
      const orderData = {
        userid: user.id,
        totalamount: gtotal,
        deliveryCharge: deliveryCharge,
        customerid: customerId, // ✅ Use from localStorage
        orderno: orderno,
        cart_items: cartreduxdata || [],
        packages: packagesreduxdata || [],
        personalised: personalisedreduxdata || [],
        customer_details: custdata?.data || {},
        products: cartreduxdata,
        shipping_info: servicedata || {},
        order_type: currentOrderType,
        delivery_id: deliveryId,
        collection_id: collectionId,
        shipping_charges: deliveryCharge, // ✅ ADD THIS
        shipping_method: servicedata?.service || "Standard Delivery", // ✅ ADD THIS
      };

      console.log("Sending to Stripe:", orderData);
      mutationStripePayment.mutate(orderData);
    } else {
      // OFFLINE ORDER PROCESSING
      const offlineOrderData = {
        userid: user.id,
        totalamount: gtotal,
        customerid: customerId, 
        orderno: orderno,
        transactionid: "OFFLINE",
        payment_method: "offline",
        products: cartreduxdata,
        delivery_charge: deliveryCharge,
        shipping_carrier: servicedata?.carrier || null,
        shipping_service: servicedata?.service || null,
        order_type: currentOrderType,
        customer_details: custdata?.data || {},
        delivery_id: deliveryId,
        collection_id: collectionId,
        shipping_charges: deliveryCharge, // ✅ ADD THIS
        shipping_method: servicedata?.service || "Standard Delivery", // ✅ ADD THIS
      };

      console.log("Placing offline order:", offlineOrderData);
      mutationorderplace.mutate(offlineOrderData);
    }
  };

  // Loading state
  if (!user) {
    return (
      <div className="checkoutPaymentPage">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h3>Loading user information...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

   console.log("order type nwe rwererwer ",orderType);
  return (
    <div className="checkoutPaymentPage">
      <div className="container">
        <div className="row">
          <div className="col-md-2"></div>
          <div className="col-md-8">
            <OrderSummary />

            {/* Error Display */}
            {error && (
              <div
                style={{
                  background: "#ffebee",
                  color: "#c62828",
                  padding: "10px",
                  borderRadius: "5px",
                  marginBottom: "20px",
                  border: "1px solid #ef5350",
                }}
              >
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* ✅ UPDATED: Customer Information Check - Only for delivery orders */}
            {orderType === "delivery" &&
              !custdata?.data?.id &&
              !localStorage.getItem("delivery_id") && (
                <div
                  style={{
                    background: "#fff3cd",
                    color: "#856404",
                    padding: "15px",
                    borderRadius: "5px",
                    marginBottom: "20px",
                    border: "1px solid #ffeaa7",
                  }}
                >
                  <strong>Missing Information:</strong> Please add your delivery
                  details before proceeding.
                  <br />
                  <button
                    className="btn btn-warning btn-sm mt-2"
                    onClick={() => navigate("/details")}
                  >
                    Add Delivery Details
                  </button>
                </div>
              )}

            {/* ✅ NEW: Collection Information Check - Only for collection orders */}
            {orderType === "collection" &&
              !localStorage.getItem("collection_id") && (
                <div
                  style={{
                    background: "#fff3cd",
                    color: "#856404",
                    padding: "15px",
                    borderRadius: "5px",
                    marginBottom: "20px",
                    border: "1px solid #ffeaa7",
                  }}
                >
                  <strong>Missing Information:</strong> Please submit your
                  collection details before proceeding.
                  <br />
                  <button
                    className="btn btn-warning btn-sm mt-2"
                    onClick={() => navigate("/details")}
                  >
                    Add Collection Details
                  </button>
                </div>
              )}

            {/* Payment Method Selection */}
            {orderType === "delivery" ? null : (
              <div
                style={{
                  padding: "10px",
                  marginBottom: "20px",
                  background: "#f8f9fa",
                  borderRadius: "5px",
                }}
              >
                <h5>Select Payment Method</h5>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "20px",
                    flexDirection: "column",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("online")}
                    disabled={isLoading}
                    style={{
                      padding: "15px 20px",
                      border:
                        paymentMethod === "online"
                          ? "2px solid #28a745"
                          : "1px solid #ccc",
                      backgroundColor:
                        paymentMethod === "online" ? "#28a745" : "#fff",
                      color: paymentMethod === "online" ? "#fff" : "#000",
                      borderRadius: "8px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.7 : 1,
                      fontSize: "16px",
                      fontWeight:
                        paymentMethod === "online" ? "bold" : "normal",
                    }}
                  >
                    Pay Now (Credit/Debit Card)
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod("offline")}
                    disabled={isLoading}
                    style={{
                      padding: "15px 20px",
                      border:
                        paymentMethod === "offline"
                          ? "2px solid #28a745"
                          : "1px solid #ccc",
                      backgroundColor:
                        paymentMethod === "offline" ? "#28a745" : "#fff",
                      color: paymentMethod === "offline" ? "#fff" : "#000",
                      borderRadius: "8px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.7 : 1,
                      fontSize: "16px",
                      fontWeight:
                        paymentMethod === "offline" ? "bold" : "normal",
                    }}
                  >
                    {/* Collection Only (Pay Later) */}
                    Pay Later
                  </button>
                </div>
              </div>
            )}

            <div style={{ padding: "5px 10px 10px 10px" }}>
              <button
                onClick={handlePlaceOrder}
                className="btn2 w-100 mt-3"
                disabled={
                  isLoading ||
                  (orderType === "delivery" &&
                    !custdata?.data?.id &&
                    !localStorage.getItem("delivery_id")) ||
                  (orderType === "collection" &&
                    !localStorage.getItem("collection_id"))
                }
                style={{
                  opacity:
                    isLoading ||
                    (orderType === "delivery" &&
                      !custdata?.data?.id &&
                      !localStorage.getItem("delivery_id")) ||
                    (orderType === "collection" &&
                      !localStorage.getItem("collection_id"))
                      ? 0.7
                      : 1,
                  cursor:
                    isLoading ||
                    (orderType === "delivery" &&
                      !custdata?.data?.id &&
                      !localStorage.getItem("delivery_id")) ||
                    (orderType === "collection" &&
                      !localStorage.getItem("collection_id"))
                      ? "not-allowed"
                      : "pointer",
                  position: "relative",
                  padding: "15px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  backgroundColor:
                    paymentMethod === "online" ? "#28a745" : "#28a745",
                  border: "none",
                  color: "white",
                  borderRadius: "8px",
                }}
              >
                {isLoading ? (
                  <span>
                    <span style={{ marginRight: "10px" }}>Loading...</span>
                    {paymentMethod === "online"
                      ? "Creating Payment Session..."
                      : "Placing Order..."}
                  </span>
                ) : (
                  <span>
                    {paymentMethod === "online"
                      ? "Place Order"
                      : orderType === "collection"
                      ? "Place Order (Collection)"
                      : "Place Order (Delivery)"}
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "5px",
                        opacity: 0.9,
                      }}
                    >
                      Total: £{gtotal.toFixed(2)}
                    </div>
                  </span>
                )}
              </button>

              {/* Information based on payment method */}
              {paymentMethod === "offline" ? (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                    marginTop: "10px",
                    background: "#fff3cd",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ffeaa7",
                  }}
                >
                  <strong>Collection Information:</strong>
                  <br />
                  Your order will be prepared for collection. You can collect
                  your materials from our warehouse at:
                  <strong>
                    {" "}
                    25 Cabot Square, Canary Wharf, Greater London E14 4QZ, United Kingdom
                  </strong>
                  <br />
                  <br />
                  Payment can be made upon collection.
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                    marginTop: "10px",
                    background: "#fff3cd",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ffeaa7",
                  }}
                >
                  <strong>Secure Online Payment:</strong>
                  <br />
                  You will be redirected to Stripe's secure payment page to
                  complete your purchase with your credit or debit card. Your
                  payment information is encrypted and secure.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckOut;
